import {
  AccessGrantState,
  AccessGrantStatus,
  DashboardTextFilterType,
  RequirementStatus,
  RequirementType,
} from '../../../__generated__/graphql';

import {
  FilterDashboardAccessGrantsQueryArgs,
  FilterDashboardAccessGrantsQuerySearchParameters,
} from '..';
import { DataTableFilterValues } from '../../../@types/client/DataTableFilterValues';
import moment from 'moment';

export default function parseAccessGrantsFilterParameters(
  fieldsFiltered: DataTableFilterValues[],
  searchParameters: FilterDashboardAccessGrantsQuerySearchParameters
): Partial<FilterDashboardAccessGrantsQueryArgs> {
  const filterParams: Partial<FilterDashboardAccessGrantsQueryArgs> = {};

  if (fieldsFiltered?.length > 0) {
    // Location filter
    const locFieldsIndex = fieldsFiltered.findIndex((field) => field.fieldKey === 'loc');
    if (locFieldsIndex > -1) {
      const inInput = fieldsFiltered[locFieldsIndex].values;

      filterParams.locationId = { in: inInput };
    }

    // ID Verification filter
    const ivsFieldsIndex = fieldsFiltered.findIndex((field) => field.fieldKey === 'ivs');
    if (ivsFieldsIndex > -1) {
      const inInput = fieldsFiltered[ivsFieldsIndex].values as RequirementStatus[];

      filterParams.requirementType = { in: [RequirementType.IdVerification] };
      filterParams.requirementStatus = { in: inInput };
    }

    // Custom default filter for RoomNumber
    if (searchParameters.textFilter?.type === DashboardTextFilterType.RoomNumber) {
      if (!filterParams.timeFilter) {
        filterParams.timeFilter = {};
      }
      filterParams.timeFilter.startsAt = {
        to: moment().endOf('day').toDate(),
      };
      filterParams.timeFilter.endsAt = {
        from: moment().startOf('day').toDate(),
      };
    }

    const ciFieldsIndex = fieldsFiltered.findIndex((field) => field.fieldKey === 'ci');
    const coFieldsIndex = fieldsFiltered.findIndex((field) => field.fieldKey === 'co');
    if (ciFieldsIndex > -1 && filterParams.timeFilter) {
      // Resetting the endsAt default filter for RoomNumber if Check-in filter is set
      filterParams.timeFilter.endsAt = undefined;
    }
    if (coFieldsIndex > -1 && filterParams.timeFilter) {
      // Resetting the startsAt default filter for RoomNumber if Check-out filter is set
      filterParams.timeFilter.startsAt = undefined;
    }

    // Check-in filter
    if (ciFieldsIndex > -1) {
      const checkInParams = fieldsFiltered[ciFieldsIndex].values.map(
        (num) => parseInt(num, 10) ?? 0
      );
      const maxCheckIn = Math.max(...checkInParams);
      const minCheckIn = Math.min(...checkInParams);

      if (minCheckIn < 0) {
        const gtInput = minCheckIn * 1000 + Date.now();
        const ltInput = Math.max(maxCheckIn, 0) * 1000 + Date.now();
        if (!filterParams.timeFilter) {
          filterParams.timeFilter = {};
        }
        filterParams.timeFilter.startsAt = {
          from: new Date(gtInput),
          to: new Date(ltInput),
        };
      } else {
        const ltInput = maxCheckIn * 1000 + Date.now();
        if (!filterParams.timeFilter) {
          filterParams.timeFilter = {};
        }
        filterParams.timeFilter.startsAt = {
          from: new Date(),
          to: new Date(ltInput),
        };
      }
    }

    // Check-out filter
    if (coFieldsIndex > -1) {
      const checkOutParams = fieldsFiltered[coFieldsIndex].values.map(
        (num) => parseInt(num, 10) ?? 0
      );
      const maxCheckOut = Math.max(...checkOutParams);
      const ltInput = maxCheckOut * 1000 + Date.now();

      if (!filterParams.timeFilter) {
        filterParams.timeFilter = {};
      }
      filterParams.timeFilter.endsAt = {
        from: new Date(),
        to: new Date(ltInput),
      };
    }

    // State filter
    const stateFieldsIndex = fieldsFiltered.findIndex((field) => field.fieldKey === 'state');
    if (stateFieldsIndex > -1) {
      const inInput = fieldsFiltered[stateFieldsIndex].values as AccessGrantState[];

      filterParams.state = { in: inInput };
    }

    // Status filter
    const statusFieldsIndex = fieldsFiltered.findIndex((field) => field.fieldKey === 'status');
    if (statusFieldsIndex > -1) {
      const inInput = fieldsFiltered[statusFieldsIndex].values as AccessGrantStatus[];

      filterParams.status = { in: inInput };
    }
  }

  return filterParams;
}
